import type { Rate } from '@geo-admin/shared/contract/common.ts';
import { customerRate, MIN_SAMPLES, rateText } from '../lib/format.ts';
import { cn } from '../lib/utils.ts';

/**
 * 비율 한 칸. `raw` 를 주면 아래에 **실측**을 덧붙인다 — 온보딩 쓰로틀링이 걸린 값과 원래 값을
 * 나란히 보기 위한 것이고, 계약상 운영자에게만 채워진다
 * (260921-report-snapshot-and-onboarding-throttle).
 */
export function RateCell({
  rate,
  emphasize = false,
  raw = null,
  customer = false,
  plain = false,
  minSamples,
}: {
  rate: Rate | null;
  emphasize?: boolean;
  raw?: Rate | null;
  /** 고객·대행사에게 보이는 표인가 — 표본에 관한 정보를 싣지 않는다 (260921-client-report-chart-only) */
  customer?: boolean;
  /**
   * 운영자 표에서 비율만 보인다 — `k/n`·95% CI·표본 부족 문구를 싣지 않는다(노출 현황, 운영자 결정 2026-11-03).
   * 표본이 하한 미만이면 **흐리게** 그려 얇다는 것만 남긴다.
   * `raw` 를 주면 **실측이 메인**이고 표시값(온보딩)이 아래 작은 줄이다
   */
  plain?: boolean;
  /** 표본 하한 — 생략하면 MIN_SAMPLES. 분모가 작아도 비율을 보일 칸만 낮춘다 (rateText 주석) */
  minSamples?: number;
}) {
  if (!rate) return <span className="text-slate-400">—</span>;
  // ★ 고객에게는 k/n·95% CI·표본 부족을 보이지 않는다. 비율 한 줄뿐이다
  if (customer) return <div className="tabular-nums">{customerRate(rate)}</div>;
  if (plain) {
    // ★ 여기서는 **실측이 메인**이고 온보딩 계수를 먹인 표시값이 아래 작은 줄이다(운영자 결정 2026-11-03).
    //   계수가 1 이면(온보딩 밖) 두 값이 같다 — 같은 숫자를 두 번 보이지 않는다
    const onboarding = raw && raw.k !== rate.k ? customerRate(rate) : null;
    const main = raw ?? rate;
    const thin = main.n < (minSamples ?? MIN_SAMPLES);
    return (
      <div className="leading-tight">
        <div className={cn('tabular-nums', emphasize && !thin && 'text-base font-semibold', thin && 'text-slate-400')}>
          {customerRate(main)}
        </div>
        {onboarding ? <div className="text-xs tabular-nums text-slate-500">온보딩 {onboarding}</div> : null}
      </div>
    );
  }
  const t = rateText(rate, minSamples);
  // 계수가 1 이면(온보딩 밖) 표시값과 실측이 같다 — 같은 숫자를 두 번 보이지 않는다
  const r = raw && raw.k !== rate.k ? rateText(raw) : null;
  return (
    <div className="leading-tight">
      <div
        className={cn(
          'tabular-nums',
          emphasize && !t.insufficient && 'text-base font-semibold',
          t.insufficient && 'text-slate-500',
        )}
      >
        {t.main}
      </div>
      {t.sub ? (
        <div className={cn('text-xs tabular-nums', t.insufficient ? 'text-warn' : 'text-slate-500')}>{t.sub}</div>
      ) : null}
      {r ? <div className="text-xs tabular-nums text-ours">실측 {r.main}</div> : null}
    </div>
  );
}
